import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import type { Chat } from "../../redux/reducers/ChatReducer/ChatSliceTypes";
import {
  useResponsive,
  useFileUpload,
  useChatList,
  useMessages,
  useBookingActions,
} from "./hooks";
import ChatSidebar from "./components/ChatSidebar/ChatSidebar";
import ChatWindow from "./components/ChatWindow/ChatWindow";
import DetailsPanel from "./components/DetailsPanel/DetailsPanel";

/**
 * Messages Component
 * Main container that orchestrates the entire messaging interface
 * Reduced from 800+ lines to ~100 lines through modular architecture
 */
const Messages: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);

  // Custom hooks for business logic
  const responsive = useResponsive();
  const fileUpload = useFileUpload(selectedChat?.id.toString() || null);
  const chatList = useChatList();
  const messages = useMessages(selectedChat?.id.toString() || null);
  const bookingActions = useBookingActions();


const handleChatSelect = async (chat: Chat) => {
  console.log("🔵 Chat selected:", chat.id, "Unread:", chat.unread_count);
  
  setSelectedChat(chat);
  responsive.handleChatSelect();
  
  // ✅ OPEN DETAILS PANEL ON DESKTOP when chat is selected
  if (!responsive.isMobile) {
    responsive.setShowDetails(true); // Add this line
  }
  
  // Reset unread count
  await messages.resetSelectedChatUnreadCount(chat.id.toString());
  
  // ✅ CRITICAL: Refresh the chat list to update badges in sidebar
  try {
    await chatList.fetchChatLists();
    console.log("🟢 Chat list refreshed");
  } catch (error) {
    console.error("🔴 Failed to refresh chat list:", error);
  }
};

  // Add this function after handleChatSelect
  const handleFilterChange = (newFilter: "all" | "unread") => {
    console.log("🔍 Messages: Filter changed to:", newFilter);
    console.log("📱 Mobile:", responsive.isMobile);
    console.log("💬 Selected chat:", selectedChat?.id);

    // Apply the filter through the hook
    chatList.handleFilterChange(newFilter);

    // Close chat on mobile
    if (responsive.isMobile && selectedChat) {
      console.log("✅ Closing chat and going back to list");
      setSelectedChat(null);
      responsive.handleBackToList();
    }
  };

  // Prevent mobile browser swipe-back gesture and pull-to-refresh
  useEffect(() => {
    // Prevent overscroll behaviors (pull-to-refresh, bounce)
    document.body.style.overscrollBehavior = "none";

    // For iOS Safari specifically
    const preventOverscroll = (e: TouchEvent) => {
      // Allow normal scrolling within scrollable containers
      let target = e.target as HTMLElement;
      while (target && target !== document.body) {
        const style = window.getComputedStyle(target);
        if (style.overflowY === "auto" || style.overflowY === "scroll") {
          return; // Allow scrolling in scrollable containers
        }
        target = target.parentElement as HTMLElement;
      }

      // Prevent default for touches outside scrollable containers
      if (e.cancelable) {
        e.preventDefault();
      }
    };

    document.addEventListener("touchmove", preventOverscroll, {
      passive: false,
    });

    return () => {
      document.body.style.overscrollBehavior = "auto";
      document.removeEventListener("touchmove", preventOverscroll);
    };
  }, []);

  // Debug selected chat
  useEffect(() => {
    console.log("=== DEBUG SELECTED CHAT ===");
    console.log("selectedChat:", selectedChat);
    console.log("isMobile:", responsive.isMobile);
    console.log("showSidebar:", responsive.showSidebar);
  }, [selectedChat, responsive.isMobile, responsive.showSidebar]);

  // Handle message sending with file cleanup
  const handleSendMessage = () => {
    messages.handleSendMessage(
      selectedChat,
      messages.message,
      fileUpload.attachments,
      fileUpload.clearAttachments,
      (msg, att) => {
        messages.setMessage(msg);
        fileUpload.setAttachments(att);
      }
    );
  };

  return (
    <div
      className="h-screen bg-neutral-50 dark:bg-neutral-900"
      style={{
        overscrollBehavior: "none",
        touchAction: "pan-y pinch-zoom",
      }}
    >
      <div className="flex flex-col md:flex-row h-full max-w-[2000px] mx-auto">
        {/* Chat Sidebar - Left Panel */}
        {responsive.showSidebar && (
          <ChatSidebar
            currentUser={currentUser}
            chats={chatList.chats}
            selectedChat={selectedChat}
            filter={chatList.filter}
            searchQuery={chatList.searchQuery}
            showSearch={chatList.showSearch}
            loading={chatList.loading}
            isLoadingMore={chatList.isLoadingMore}
            searchInputRef={chatList.searchInputRef}
            onChatSelect={handleChatSelect}
            onFilterChange={handleFilterChange}
            onSearchChange={chatList.setSearchQuery}
            onClearSearch={chatList.clearSearch}
            onToggleSearch={() => chatList.setShowSearch(!chatList.showSearch)}
            onLoadMore={chatList.loadMoreChats}
            isMobile={responsive.isMobile}
          />
        )}

        {/* Chat Window - Center Panel */}
        {(!responsive.isMobile || selectedChat) && (
          <ChatWindow
            selectedChat={selectedChat}
            messages={messages.messages}
            currentUser={currentUser}
            message={messages.message}
            attachments={fileUpload.attachments}
            filePreviews={fileUpload.filePreviews}
            isUploading={messages.isUploading}
            messageLoading={messages.messageLoading}
            page={messages.page}
            totalPages={messages.totalPages}
            onMessageChange={messages.setMessage}
            onSendMessage={handleSendMessage}
            onFileChange={fileUpload.handleFileChange}
            onRemovePreview={fileUpload.handleRemovePreview}
            onScroll={messages.handleScroll}
            onBackToList={() => {
              setSelectedChat(null);
              messages.setMessages([]); // Clear messages
              responsive.handleBackToList();
            }}
            onToggleDetails={responsive.toggleDetails}
            messageContainerRef={messages.messageContainerRef}
            fileInputRef={fileUpload.fileInputRef}
            setMessages={messages.setMessages}
            isMobile={responsive.isMobile}
            showDetails={responsive.showDetails}
          />
        )}

        {/* Details Panel - Right Panel */}
        {responsive.showDetails && selectedChat && (
          <DetailsPanel
            selectedChat={selectedChat}
            currentUser={currentUser}
            bookingId={(selectedChat as any).booking?.id || null}
            bookingQueryId={(selectedChat as any).booking_query?.id || null}
            booking={(selectedChat as any).booking || null}
            bookingQuery={(selectedChat as any).booking_query || null}
            onProceedToBooking={() =>
              bookingActions.handleProceedToBooking(
                (selectedChat as any).booking_query,
                (selectedChat as any).booking_query?.id,
                selectedChat
              )
            }
            onProceedToPayment={() =>
              bookingActions.handleProceedToPayment(
                (selectedChat as any).booking?.id
              )
            }
            onClose={responsive.toggleDetails}
            isMobile={responsive.isMobile}
          />
        )}
      </div>
    </div>
  );
};

export default Messages;
